import { useCallback, useContext, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { Context } from "../App";
import { removeSpacesFromString } from "../utils/stringUtils";
import { messages } from "../constants/messages";
import { replaceAttributeCharsInParsedJson } from "../utils/helpers";
import { useMultiSchema } from "../schema/schemaContext";
import { coerceIfLegacyTopLevelBundle } from "../utils/packageUtils";
import { parseOcaZipArrayBuffer } from "../utils/ocaZipImport";

const useHandleAllDrop = () => {
  const {
    setCurrentPage,
    setSummaryExportMode,
    setZipToReadme,
    setJsonToReadme,
    excelSheetChoice,
    setExcelSheetChoice
  } = useContext(Context);

  const [fileData, setFileData] = useState([]);
  const [rawFile, setRawFile] = useState([]);
  const {
    clearAllSchemas,
    switchToSchema,
    loadAllSchemasFromOcaPackage,
    setOcaPackage,
    updateSchema
  } = useMultiSchema();
  // useZipParser removed - data processing now handled by loadAllSchemasFromOcaPackage -> OCAParser

  const [loading, setLoading] = useState(false);
  const [dropDisabled, setDropDisabled] = useState(false);
  const [dropMessage, setDropMessage] = useState({ message: "", type: "" });
  const [excelSheetNames, setExcelSheetNames] = useState([]);
  const [tempExcel, setTempExcel] = useState(null);

  // current fileData structure: [[tableHeading, [tableValues]], [tableHeading, [tableValues]], [tableHeading, [tableValues]], ...etc]
  const processExcelFile = useCallback(
    (workbook, index = 0) => {
      const sheet_name_list = workbook.SheetNames[index];
      const jsonFromExcel = XLSX.utils.sheet_to_json(workbook.Sheets[sheet_name_list], {
        raw: false,
        dateNF: "MM-DD-YYYY",
        header: 1,
        defval: ""
      });

      const rowsArray = jsonFromExcel[0];
      if (!rowsArray) {
        setDropMessage({
          message: messages.noDataUploadFail,
          type: "error"
        });
        setLoading(false);
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [2500]);

        return;
      }

      // format element: [[attribute name, [table values]], [attribute name, [table values]], [attribute name, [table values]]]
      const dataArray = [];
      let blanks = false;

      rowsArray.forEach((value, index) => {
        const valuesArray = [];
        const noSpacesValue = removeSpacesFromString(value);
        const allEmpty = (array) => {
          let result = true;
          array.forEach((item) => {
            if (item !== "") {
              result = false;
            }
          });
          return result;
        };
        jsonFromExcel.forEach((val, subIndex) => {
          if (subIndex > 0) {
            valuesArray.push(val[index]);
          }
        });
        if (valuesArray.length) {
          if (!value && !allEmpty(valuesArray)) {
            blanks = true;
            dataArray.push(["", valuesArray]);
          }
          if (value && allEmpty(valuesArray)) {
            dataArray.push([noSpacesValue, []]);
          }
          if (value && !allEmpty(valuesArray)) {
            dataArray.push([noSpacesValue, valuesArray]);
          }
        } else {
          dataArray.push([noSpacesValue, []]);
        }
      });
      setFileData(dataArray);
      setLoading(false);
      setDropDisabled(true);
      setDropMessage({
        message: messages.successfulUpload,
        type: "success"
      });

      if (blanks) {
        setTimeout(() => {
          setDropMessage({
            message: messages.blankEntries,
            type: "info"
          });
        }, [500]);
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [3500]);
      } else {
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [2500]);
      }
      return dataArray;
    },
    [setFileData]
  );

  const processCSVFile = useCallback(
    (file) => {
      try {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: "greedy",
          transformHeader: (header, index) => {
            if (header !== "") {
              return header;
            }
            // without this, papaparse will save blank headers as "", "_1", "_2", etc.
            return `header_empty_placeholder_${index}`;
          },
          complete: (results) => {
            if (!results.data[0] && !results.meta.fields) {
              setDropMessage({
                message: messages.noDataUploadFail,
                type: "error"
              });
              setLoading(false);
              setTimeout(() => {
                setDropMessage({ message: "", type: "" });
              }, [2500]);

              return;
            }

            const findLongest = (arr1, arr2) => {
              const result = arr1.length > arr2.length ? arr1 : arr2;
              return result;
            };

            let rowsArray;

            // in some cases, results.data[0] is undefined, and headers are usually (but not always) present in results.meta.fields
            if (results.data[0]) {
              rowsArray = findLongest(results.meta.fields, Object.keys(results.data[0]));
            } else {
              rowsArray = results.meta.fields;
            }

            if (!results.data[0]) {
              let allBlank = true;
              rowsArray.forEach((value) => {
                if (
                  !value.includes("header_empty_placeholder_") &&
                  !value.includes("__parsed_extra")
                ) {
                  allBlank = false;
                }
              });
              if (allBlank === true) {
                setDropMessage({
                  message: messages.noDataUploadFail,
                  type: "error"
                });
                setLoading(false);
                setTimeout(() => {
                  setDropMessage({ message: "", type: "" });
                }, [2500]);

                return;
              }
            }

            // create dataArray structure: [[attribute name, [table values]], [attribute name, [table values]], [attribute name, [table values]]]

            const dataArray = [];
            let blanks = false;
            rowsArray.forEach((value) => {
              const noSpacesAttribute = removeSpacesFromString(value);
              const valuesArray = [];
              let emptyCounter = 0;
              results.data.forEach((val) => {
                valuesArray.push(val[value]);
                if (!val[value]) {
                  emptyCounter++;
                }
              });

              const createBlankValue = () => {
                blanks = true;
                return "";
              };

              if (valuesArray.length > 0) {
                if (valuesArray.length !== emptyCounter) {
                  if (!noSpacesAttribute.includes("header_empty_placeholder_")) {
                    let newValue;
                    if (noSpacesAttribute.includes("__parsed_extra")) {
                      newValue = createBlankValue();
                    } else {
                      newValue = noSpacesAttribute;
                    }

                    dataArray.push([newValue, valuesArray]);
                  } else {
                    dataArray.push(["", valuesArray]);
                    blanks = true;
                  }
                } else if (!noSpacesAttribute.includes("header_empty_placeholder_")) {
                  dataArray.push([noSpacesAttribute, valuesArray]);
                }
              } else if (!noSpacesAttribute.includes("header_empty_placeholder_")) {
                let newValue;
                if (noSpacesAttribute.includes("__parsed_extra")) {
                  newValue = createBlankValue();
                } else {
                  newValue = noSpacesAttribute;
                }

                dataArray.push([newValue, []]);
              } else {
                dataArray.push(["", valuesArray]);
                blanks = true;
              }
            });

            setFileData(dataArray);
            setLoading(false);
            setDropDisabled(true);

            setDropMessage({
              message: messages.successfulUpload,
              type: "success"
            });

            if (blanks) {
              setTimeout(() => {
                setDropMessage({
                  message: messages.blankEntries,
                  type: "info"
                });
              }, [500]);

              setTimeout(() => {
                setDropMessage({ message: "", type: "" });
              }, [3500]);
            } else {
              setTimeout(() => {
                setDropMessage({ message: "", type: "" });
              }, [2500]);
            }
          }
        });
      } catch {
        setDropMessage({ message: messages.parseUploadFail, type: "error" });
        setLoading(false);
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [2500]);
      }
    },
    [setFileData]
  );

  const handleExcelDrop = useCallback((acceptedFiles) => {
    try {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        const rABS = !!reader.readAsBinaryString; // converts object to boolean
        reader.onabort = () => {}; // console.log("file reading was aborted");
        reader.onerror = () => {}; // console.log("file reading has failed");
        reader.onload = (e) => {
          const bstr = e.target.result;
          const workbook = XLSX.read(bstr, {
            type: rABS ? "binary" : "array"
          });
          // processExcelFile(workbook);
          setTempExcel(workbook);
          setExcelSheetNames(workbook.SheetNames);
          setExcelSheetChoice(workbook.SheetNames[0]);
          setLoading(false);
          setTimeout(() => {
            setDropMessage({ message: "", type: "" });
          }, [2500]);
        };
        if (rABS) reader.readAsBinaryString(file);
        else reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      setDropMessage({ message: messages.uploadFail, type: "error" });
      setLoading(false);
      setTimeout(() => {
        setDropMessage({ message: "", type: "" });
      }, [2500]);
    }
  }, []);

  const handleBundleJSONDrop = useCallback(
    (jsonFile) => {
      const languageList = [];

      if (jsonFile?.overlays?.meta) {
        languageList.push(
          ...jsonFile.overlays.meta.map((meta) => meta.language.slice(0, 2))
        );
      }

      if (!languageList || languageList.length === 0) {
        throw new Error("No language found in the JSON file");
      }

      setJsonToReadme(jsonFile);
    },
    [setJsonToReadme]
  );

  const handleZipDrop = useCallback((acceptedFiles) => {
    try {
      setLoading(true);
      // Clear multi-schema context when uploading a new file
      clearAllSchemas();

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const { ocaPackage, allZipFiles, root } = await parseOcaZipArrayBuffer(
            e.target.result
          );

          setOcaPackage(ocaPackage);
          setZipToReadme(allZipFiles);

          loadAllSchemasFromOcaPackage(ocaPackage);

          switchToSchema(root, ocaPackage);

          handleBundleJSONDrop(ocaPackage.oca_bundle.bundle);
          setLoading(false);
          setCurrentPage("View");
        } catch (error) {
          console.error("Zip upload error:", error);
          setDropMessage({ message: messages.uploadFail, type: "error" });
          setLoading(false);
          setTimeout(() => {
            setDropMessage({ message: "", type: "" });
          }, 2500);
        }
      };

      reader.readAsArrayBuffer(acceptedFiles[0]);
    } catch (error) {
      console.error("Zip upload error:", error);
      setDropMessage({ message: messages.uploadFail, type: "error" });
      setLoading(false);
      setTimeout(() => {
        setDropMessage({ message: "", type: "" });
      }, [2500]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJsonDrop = useCallback((acceptedFiles) => {
    try {
      setLoading(true);
      // Clear multi-schema context when uploading a new file
      clearAllSchemas();

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const jsonFile = coerceIfLegacyTopLevelBundle(JSON.parse(e.target.result));
          if (jsonFile?.oca_bundle?.bundle) {
            const modifiedBundle = replaceAttributeCharsInParsedJson(
              jsonFile.oca_bundle.bundle
            );
            setOcaPackage(jsonFile);

            loadAllSchemasFromOcaPackage(jsonFile);

            switchToSchema(jsonFile.oca_bundle.bundle.d, jsonFile);
            handleBundleJSONDrop(modifiedBundle, jsonFile);
          } else if (jsonFile?.schema?.[0]) {
            handleBundleJSONDrop(jsonFile?.schema?.[0]);
          } else {
            handleBundleJSONDrop(jsonFile);
          }
          setDropMessage({ message: "", type: "" });
          setLoading(false);
          setCurrentPage("View");
        } catch (error) {
          console.error("Json upload error:", error);
          setDropMessage({ message: messages.uploadFail, type: "error" });
          setLoading(false);
          setTimeout(() => {
            setDropMessage({ message: "", type: "" });
          }, 2500);
        }
      };

      reader.readAsText(acceptedFiles[0]);
    } catch (error) {
      setDropMessage({ message: messages.uploadFail, type: "error" });
      setLoading(false);
      setTimeout(() => {
        setDropMessage({ message: "", type: "" });
      }, [2500]);
    }
  }, []);

  const handlePageForward = useCallback(() => {
    let listToUpdate = fileData.map((item) => item[0]);

    if (
      rawFile &&
      rawFile.length > 0 &&
      (rawFile[0].path.includes(".xls") || rawFile[0].path.includes(".xlsx"))
    ) {
      const index = excelSheetNames.indexOf(excelSheetChoice);
      const dataArray = processExcelFile(tempExcel, index);
      if (dataArray) {
        listToUpdate = dataArray.map((item) => item[0]);
      }
    }

    if (listToUpdate && listToUpdate.length > 0) {
      const attributeRowData = listToUpdate.map((attr) => ({
        Attribute: attr,
        Type: "",
        Description: "",
        Required: false,
        EntryCodes: [],
        List: false
      }));

      // Initialize the schema with these attributes
      updateSchema({
        attributes: attributeRowData
      });
    }

    setCurrentPage("Metadata");
  }, [
    fileData,
    excelSheetChoice,
    excelSheetNames,
    setCurrentPage,
    processExcelFile,
    tempExcel,
    rawFile,
    updateSchema
  ]);

  useEffect(() => {
    if (rawFile.length > 0 && rawFile[0].size > 1000000) {
      setDropMessage({
        message: messages.fileSizeLimit,
        type: "error"
      });
      setLoading(false);
      setTimeout(() => {
        setDropMessage({ message: "", type: "" });
      }, [2500]);
    } else if (rawFile.length > 0 && rawFile[0].path.includes(".csv")) {
      processCSVFile(rawFile[0]);
    } else if (rawFile.length > 0 && rawFile[0].path.includes(".xls")) {
      handleExcelDrop(rawFile);
    } else if (rawFile.length > 0 && rawFile[0].path.includes(".zip")) {
      setSummaryExportMode(false);
      handleZipDrop(rawFile);
    } else if (rawFile.length > 0 && rawFile[0].path.includes(".json")) {
      setSummaryExportMode(false);
      handleJsonDrop(rawFile);
    } else if (rawFile.length > 0) {
      const pathOrName = (rawFile[0].path || rawFile[0].name || "").toLowerCase();
      if (pathOrName.endsWith(".yaml") || pathOrName.endsWith(".yml")) {
        setLoading(false);
        setDropDisabled(true);
      } else {
        setDropMessage({ message: messages.uploadFail, type: "error" });
        setLoading(false);
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [2500]);
      }
    }
  }, [rawFile, handleExcelDrop, handleZipDrop, processCSVFile, setFileData]);

  // Derive attributesList from fileData
  const attributesList = fileData.map((item) => item[0]);

  useEffect(() => {
    if (fileData.length > 0) {
      setDropDisabled(true);
    }
  }, [fileData]);

  // this setTimeout times the upload and will abort the process if it takes longer than 1 minute. If this happens, there is likely an uncaught issue somewhere in the parsing process
  const [timeoutId, setTimeoutId] = useState(null);
  useEffect(() => {
    if (loading) {
      const id = setTimeout(() => {
        setDropMessage({
          message: messages.tooLongUploadFail,
          type: "error"
        });
        setLoading(false);
        setDropDisabled(false);
        setFileData([]);
        setTimeout(() => {
          setDropMessage({ message: "", type: "" });
        }, [2500]);
      }, 60000);
      setTimeoutId(id);
    } else if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [loading]);

  const resetUploadState = useCallback(() => {
    setFileData([]);
    setRawFile([]);
    setDropDisabled(false);
    setLoading(false);
    setDropMessage({ message: "", type: "" });
    setExcelSheetNames([]);
    setTempExcel(null);
    setExcelSheetChoice(-1);
  }, [setExcelSheetChoice]);

  return {
    rawFile,
    setRawFile,
    attributesList,
    setLoading,
    loading,
    dropDisabled,
    dropMessage,
    setDropMessage,
    setDropDisabled,
    setFileData,
    setCurrentPage,
    resetUploadState,
    excelSheetNames,
    setExcelSheetChoice,
    setExcelSheetNames,
    excelSheetChoice,
    handlePageForward
  };
};

export default useHandleAllDrop;
